import { EMPTY_FOLDERS, resolveIdentities } from '../entity/folders';
import type { TagColorId } from '../design/tagPalette';
import { tagColorOf } from '../entity/tagColor';
import type { Folders, TagRow } from '../entity/folders';

/**
 * Tag and marker registries — what turns a bare name in an arc's front-matter
 * into something with a colour, a meaning, and somewhere to go.
 *
 * Both are optional: a vault without them renders chips untinted rather
 * than failing, which is the same thing `plan.html` does.
 */

export interface TagInfo {
  name: string;
  color: string | null;
}

export interface MarkerInfo {
  name: string;
  count: number;
  parent: string | null;
  scope: string;
}

export interface Registries {
  tags: Map<string, TagInfo>;
  markers: Map<string, MarkerInfo>;
  folders: Folders;
}

/** The tag registry as a single note. RETIRED upstream on 2026-08-24 —
 *  `FileVaultExporter.WRITE_LEGACY_TAG_REGISTRY` is false, so a folder minted
 *  since is absent from it. Still read, so a vault no shard write has reached
 *  keeps its tints. */
export const TAGS_PATH = '_tags.md';
export const MARKERS_PATH = '_markers/registry.md';

export const EMPTY_REGISTRIES: Registries = { tags: new Map(), markers: new Map(), folders: EMPTY_FOLDERS };

export function parseTags(text: string): Map<string, TagInfo> {
  const tags = new Map<string, TagInfo>();
  for (const m of text.matchAll(/^-\s+\*\*(.+?)\*\*(?:\s+`([\w#-]+)`)?/gm)) {
    const name = m[1]?.trim();
    if (name) tags.set(name.toLowerCase(), { name, color: m[2] ?? null });
  }
  return tags;
}

/**
 * The same map from the shards. Where two rows carry one name — 249 do — the
 * CURATED one wins, so a folder's chip wears the folder's colour rather than
 * whichever machine-minted twin was read last.
 *
 * The colour is the INHERITED one, so an arc tagged `Web head` is tinted by
 * `ponder` above it rather than rendering untinted. Only 8 of the 33 folders
 * carry a colour of their own, so without this most of the tree is colourless
 * on the web and coloured on the phone.
 */
export function tagsFromRows(rows: readonly TagRow[]): Map<string, TagInfo> {
  const identities = resolveIdentities(rows);
  const tags = new Map<string, TagInfo>();
  const curated = new Set<string>();
  for (const row of rows) {
    const name = row.n?.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (curated.has(key) && !row.cur) continue;
    if (row.cur) curated.add(key);
    tags.set(key, { name, color: identities.get(row.id)?.color ?? null });
  }
  return tags;
}

export function parseMarkers(text: string): Map<string, MarkerInfo> {
  const markers = new Map<string, MarkerInfo>();
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const b = lines[i]!.match(/^-\s+\*\*(.+?)\*\*(?:\s+×(\d+))?(?:\s+↳\s+(.+?))?\s*$/);
    if (!b) continue;
    // A marker's scope sits on the next line, indented and not itself a
    // bullet — that is the only thing distinguishing it from the next entry.
    const next = lines[i + 1] ?? '';
    const scope = /^\s{2,}\S/.test(next) && !/^\s*-/.test(next) ? next.trim() : '';
    markers.set(b[1]!.trim().toLowerCase(), {
      name: b[1]!.trim(),
      count: Number(b[2] ?? 0),
      parent: b[3] ?? null,
      scope,
    });
  }
  return markers;
}

/** A tag's colour, by name: one of the palette's, or null for none. */
export function tagTint(registries: Registries, name: string): TagColorId | null {
  return tagColorOf(registries.tags.get(name.toLowerCase())?.color);
}
